import { useMemo } from 'react';

import {
  getSellAndBuyRates,
  makePairKey,
} from '@/components/TransactionModule/LimitMode/useLimitOrderRate';
import {
  taggedLimitRateSelector,
  useLimitRateStore,
} from '@/components/TransactionModule/limitRateStore';
import {
  getPriceInputsForUsdPriceFetch,
  getTokenPrices,
} from '@/components/TransactionModule/utils';

import { useUSDPrices } from '@/hooks/useUSDPrices';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { isUsdStableAddress } from '@/utils/multichain';

type UseLimitPriceLineParams = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  side: 'sell' | 'buy';
  enabled: boolean;
};

type LimitPriceLine = {
  /** The limit rate in USD per charted token, when it can be drawn. */
  priceUsd: number | undefined;
  /** Writes a dragged USD price back to the pair rate; present whenever `priceUsd` is. */
  setPriceUsd?: (priceUsd: number) => void;
};

export const useLimitPriceLine = ({
  sellToken,
  buyToken,
  side,
  enabled,
}: UseLimitPriceLineParams): LimitPriceLine => {
  const taggedRate = useLimitRateStore(taggedLimitRateSelector);
  const setTaggedRate = useLimitRateStore((state) => state.setTaggedLimitRate);
  const pairKey = useMemo(() => makePairKey(sellToken, buyToken), [buyToken, sellToken]);
  const isReady = !!pairKey && taggedRate.pairKey === pairKey && taggedRate.rate.sell !== '';
  // On a USD-pegged charted token the converted trigger lands near $1 regardless of the limit,
  // so it says nothing and stretches the y-axis of an otherwise flat series.
  const chartToken = side === 'buy' ? buyToken : sellToken;
  const shouldShowLine = enabled && !isUsdStableAddress(chartToken?.address, chartToken?.chainId);
  const priceInputs = useMemo(
    () =>
      shouldShowLine
        ? getPriceInputsForUsdPriceFetch(sellToken, buyToken, sellToken?.issuer, buyToken?.issuer)
        : undefined,
    [buyToken, sellToken, shouldShowLine],
  );
  const { data: usdPrices } = useUSDPrices(priceInputs);

  if (!shouldShowLine || !isReady || !usdPrices) return { priceUsd: undefined };

  const { sellTokenPrice, buyTokenPrice } = getTokenPrices(usdPrices, sellToken, buyToken);
  const rateValue = Number(side === 'sell' ? taggedRate.rate.sell : taggedRate.rate.buy);
  const counterTokenUsd = side === 'sell' ? buyTokenPrice?.priceUsd : sellTokenPrice?.priceUsd;
  const value = rateValue * (counterTokenUsd ?? 0);
  if (!counterTokenUsd || !Number.isFinite(value) || value <= 0) return { priceUsd: undefined };

  return {
    priceUsd: value,
    setPriceUsd: (priceUsd) => {
      // Pointer positions map to arbitrary floats; trim to chart-scale precision.
      const rate = String(Number((priceUsd / counterTokenUsd).toPrecision(6)));
      const { sellRate, buyRate } = getSellAndBuyRates(rate, side);
      setTaggedRate({ sell: sellRate, buy: buyRate }, pairKey);
    },
  };
};
